import type { ReactNode } from 'react';
import { useAuth } from '../auth/useAuth.ts';
import { DeadlineCountdown } from './DeadlineCountdown.tsx';
import { MainNav } from './MainNav.tsx';
import { SkipLink, MAIN_CONTENT_ID } from './SkipLink.tsx';
import { SquadContextBar } from './SquadContextBar.tsx';

interface AppShellProps {
  children: ReactNode;
}

/**
 * The masthead and top nav (T-048, nav reworked by T-082) — everything the
 * app can do used to be stacked on one scrolling page under here; now each
 * surface is its own route, and this is what stays on screen no matter which
 * one is showing. `SquadContextBar` sits just below the nav so the loaded
 * squad (or the lack of one) is never more than a glance away, whichever
 * route is active.
 *
 * The skip link (T-100, extracted into `SkipLink` for T-102) is the first
 * thing in the DOM, ahead of the masthead, nav and context bar it exists to
 * let a keyboard or switch user jump past — every route renders all three
 * above `<main>`.
 */
export function AppShell({ children }: AppShellProps) {
  const { logout } = useAuth();

  return (
    <div className="shell">
      <SkipLink />

      <header className="masthead">
        <h1 className="wordmark">
          The <em>Gaffer</em>
        </h1>
        <DeadlineCountdown />
        <MainNav />
        <button type="button" className="ghost" onClick={logout}>
          Sign out
        </button>
      </header>

      <SquadContextBar />

      <main id={MAIN_CONTENT_ID} tabIndex={-1}>
        {children}
      </main>

      <footer>
        Not affiliated with the Premier League or Fantasy Premier League. Advice is generated and
        can be wrong — you are still the manager.
      </footer>
    </div>
  );
}
